import React, { useState, useEffect, useMemo } from "react";
import "./Tickets.scss";
import { MoreOutlined } from "@ant-design/icons";
import {
  Button,
  ConfigProvider,
  Dropdown,
  InputNumber,
  Menu,
  Pagination,
  Skeleton,
  Table,
  Tabs,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router";
import { useUpdateTicketStatus } from "../../hooks/useUpdateTicketStatus";
import { message } from "antd";

import {
  EyeIcon,
  MergedTicketIcon,
  PrimaryTicketIcon,
  RejectedIcon,
  Ticket,
} from "../../icons";
import MergeTickets from "../../components/DrawerLayout/components/MergeTickets";
import FilterDropdown from "../../components/FilterDropdown";
import TagComp from "../../components/tag";
import { useTickets } from "../../hooks/useTickets";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import { isPermit } from "../../utils/permissionCheck";
import PermissionDenied from "../OtherPage/PermissionDenied";
import ConfirmModal from "../../components/ModalLayout/components/ConfirmModal";
import { useAssignTicket } from "../../hooks/useAssignTicket";
import ChangeStatusModal from "../../components/ModalLayout/components/ChangeStatusModal";

dayjs.extend(duration);
dayjs.extend(utc);
interface Ticket {
  // From API response
  pikitup_ticket_id: number;
  service_ticket_no: string;
  service_short_description: string;
  service_address_info: string;
  service_type: string | number;
  admin_internal_status?: string;
  status_flg: number;
  assignee_name?: string;
  created_date?: string;
  no_of_childs?: string | number;
  is_child?: boolean;
  parent_ticket_id?: number | null;

  // Legacy fields (keep for compatibility)
  id?: number | string;
  title?: string;
  location?: string;
  ticketType?: string;
  createdBy?: string;
  status?: string;
  assignedTo?: string;
  createdOn?: string;
  ticketFlag?: "rejected" | "merged" | "primary" | null;

  // Other API fields you might need
  customer_first_name?: string;
  customer_surname?: string;
  service_province?: string;
  unit_group_name?: string;
  depot_name?: string;
  response_sla_escalation_time?: string;
  response_sla_overdue?: boolean;
  resolution_sla_escalation_time?: string;
  resolution_sla_overdue?: boolean;
  created_time?: string;
}
export const STATUS_TABS: Record<string, { status_in?: string[] }> = {
  all: {},
  unassigned: { status_in: ["1"] },
  pending: { status_in: ["28"] },
  ongoing: { status_in: ["10"] },
  resolved: { status_in: ["6"] },
  rejected: { status_in: ["23"] },
  completed: { status_in: ["31"] },
  closed: { status_in: ["3"] },
};

export const STATUS_MAP: Record<string, string> = {
  "1": "New",
  "28": "Not Initiated",
  "10": "Initiated",
  "31": "Parked",
  "23": "Rejected",
  "6": "Resolved",
  "3": "Closed",
};

// --- COMPONENTS ---
const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const label = STATUS_MAP[status] || "Unassigned";
  const statusClass = label.toLowerCase().replace(/\s+/g, "-");
  return <span className={`status-pill status-${statusClass}`}>{label}</span>;
};

export const BASE_TAB_ITEMS = [
  { key: "all", label: "All" },
  { key: "unassigned", label: "New" },
  { key: "pending", label: "Not Initiated" },
  { key: "ongoing", label: "Initiated" },
  { key: "completed", label: "Parked" },
  { key: "rejected", label: "Rejected" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
];

const renderFlagIcon = (record: Ticket) => {
  // Check if it's a child ticket
  if (record.is_child) {
    return <MergedTicketIcon style={{ width: 16, height: 16 }} />;
  }

  // Check if it's a parent ticket with children
  if (record.no_of_childs && parseInt(record.no_of_childs.toString()) > 0) {
    return <PrimaryTicketIcon style={{ width: 16, height: 16 }} />;
  }

  // Fallback to original ticketFlag logic
  switch (record.ticketFlag) {
    case "rejected":
      return <RejectedIcon style={{ width: 8, height: 8 }} />;
    case "merged":
      return <MergedTicketIcon style={{ width: 16, height: 16 }} />;
    case "primary":
      return <PrimaryTicketIcon style={{ width: 16, height: 16 }} />;
    default:
      return null;
  }
};

// --- MAIN COMPONENT ---
interface TicketsTableProps {
  isMobile?: boolean;
  dataSource?: Ticket[];
  pagination?: boolean;
  scroll?: { x?: number; y?: number };
}

const TicketsTable: React.FC<TicketsTableProps> = ({
  pagination = true,
  dataSource,
}) => {
  const navigate = useNavigate();

  const ActionMenu = (record: Ticket) => {
    const items = [
      {
        key: "view",
        label: (
          <span className="flex justify-start items-center">
            <EyeIcon style={{ width: 16, height: 16, marginRight: 8 }} />
            View Details
          </span>
        ),
        onClick: () =>
          navigate(`/tickets/d/${record.pikitup_ticket_id}`, {
            state: { record },
          }),
      },
    ];

    // Show Assign Ticket only for "new" tickets
    // if (Number(record.admin_internal_status) === 1) {
    //   items.push({
    //     key: "assign",
    //     label: (
    //       <span className="flex justify-start items-center">
    //         <span style={{ marginRight: 8 }}>
    //           <AssignIcon />
    //         </span>
    //         Assign Ticket
    //       </span>
    //     ),
    //     onClick: () => {
    //       console.log("Assign Ticket clicked:", record);

    //       if (record) {
    //         // ✅ Store both key and full record
    //         setSelectedRowKeys([record.id]);
    //         setSelectedRows([record]);
    //         setAssignConfirmOpen(true);
    //       }
    //     },
    //   });
    // }

    // const EXCLUDED_STATUSES = ["Rejected", "Closed", "Resolved"];

    // Show Change Status only for rejected, closed, resolved
    // if (
    //   !EXCLUDED_STATUSES.includes(
    //     STATUS_MAP[record.admin_internal_status] || ""
    //   )
    // ) {
    //   items.push({
    //     key: "change_status",
    //     label: (
    //       <span className="flex justify-start items-center">
    //         <span style={{ marginRight: 8 }}>
    //           <StatusIcon />
    //         </span>
    //         Change Status
    //       </span>
    //     ),
    //     onClick: () => {
    //       setSelectedRecord(record);
    //       setChangeStatusModalOpen(true);
    //     },
    //   });
    // }

    return <Menu items={items} />;
  };
  const { mutate: updateTicketStatusMutate, isPending: isUpdating } =
    useUpdateTicketStatus();

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<Ticket[]>([]);
  const [filterData, setFilterData] = useState<Record<string, any>>({});
  const [mergedDrawerOpen, setMergedDrawerOpen] = useState(false);
  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [assignConfirmOpen, setAssignConfirmOpen] = useState(false);

  // const handleAssignClick = () => {
  //   if (selectedRowKeys.length === 0) {
  //     // Optional: Show notification if no tickets are selected
  //     return;
  //   }
  //   setAssignConfirmOpen(true);
  // };
  const [filters, setFilters] = useState<{
    search?: string;
    ticketType?: string | null;
    columnFilters?: Record<string, any>;
  }>({
    search: "",
    ticketType: null,
    columnFilters: {},
  });

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);
    return () => clearTimeout(handler);
  }, [filters.search]);

  const storedUserRaw = localStorage.getItem("Data");
  let storedUser: any = {};
  try {
    storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : {};
  } catch (e) {
    storedUser = {};
  }

  const memberType = storedUser?.data?.member_type ?? storedUser?.membertype;

  // handle both shapes
  const isMemberType4 = Number(memberType) === 4;
  const [activeTab, setActiveTab] = useState<string>("all");
  // Supervisor tab config
  const SUPERVISOR_TABS = [
    { key: "all", label: "All", status: "-1" },
    { key: "new", label: "New Tickets", status: "28" },
    { key: "assigned", label: "Assigned Tickets", status: "-2" },
    { key: "myAssigned", label: "My Assigned Tickets", status: "10" }, // Not Initiated + Initiated
    { key: "resolved", label: "My Resolved Tickets", status: "6" },
    { key: "rejected", label: "My Rejected Tickets", status: "23" },
    { key: "parked", label: "My Parked Tickets", status: "31" },
  ];
  const SUPERVISOR_STATUS_TABS: Record<string, { status?: string[] }> =
    SUPERVISOR_TABS.reduce((acc, t) => {
      acc[t.key] = { status: t.status ? [t.status] : undefined };
      return acc;
    }, {} as Record<string, { status?: string[] }>);

  // effective status tabs mapping: supervisor mapping when membertype=4, otherwise global STATUS_TABS
  const EFFECTIVE_STATUS_TABS = useMemo(() => {
    return isMemberType4 ? SUPERVISOR_STATUS_TABS : STATUS_TABS;
  }, [isMemberType4]);

  const [totalPageCount, setTotalPageCount] = useState(1);

  const TICKET_TYPE_API_MAP: Record<string, string> = {
    Incident: "2",
    "Service request": "1",
  };

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      perPage: pageSize,
      type: "all" as "all" | "assigned",
      service_ticket_no: "", // Add default or controlled value here
      ...(EFFECTIVE_STATUS_TABS[
        activeTab as keyof typeof EFFECTIVE_STATUS_TABS
      ] ?? {}),
      ...(filters.ticketType
        ? { ticket_type_in: [TICKET_TYPE_API_MAP[filters.ticketType]] }
        : {}),
      ...(debouncedSearch ? { keyword: debouncedSearch, search: true } : {}),
      ...(filters.columnFilters || {}),
      ...filterData,
    }),
    [
      currentPage,
      pageSize,
      activeTab,
      filters.ticketType,
      debouncedSearch,
      filters.columnFilters,
      filterData,
      EFFECTIVE_STATUS_TABS,
    ]
  );

  const { mutate: assignTicketMutate, isPending: isAssigning } =
    useAssignTicket();

  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchTickets,
  } = useTickets(queryParams, true, currentPage, pageSize);

  const total = data?.meta?.total ?? 0;

  const [allTabTotal, setAllTabTotal] = useState<any>(total);
  const [allTotalPagination, setAllTotalPagination] = useState(total);
  // console.log(
  //   total,
  //   allTotalPagination,
  //   "allTotalPaginationallTotalPagination"
  // );

  useEffect(() => {
    if (activeTab === "all" && currentPage === 1) {
      setAllTabTotal(total);
      setAllTotalPagination(data?.meta?.total);
    } else if (currentPage === 1) {
      setAllTotalPagination(data?.meta?.total ? data?.meta?.total : 1);
    }
  }, [total, activeTab]);

  const tableData = data?.tickets || [];
  const loadingState = isLoading || isFetching;

  const normalizedData = loadingState
    ? Array.from({ length: 10 }).map((_, i) => ({
        id: `skeleton-${i}-${Date.now()}-${Math.random()}`,
        pikitup_ticket_id: `skeleton-${i}-${Date.now()}-${Math.random()}`,
        service_ticket_no: "",
        service_short_description: "",
        service_address_info: "",
        assignee_name: "",
        created_date: "",
      }))
    : tableData;

  const tabItemsWithCounts = useMemo(() => {
    return BASE_TAB_ITEMS.map((item) => {
      if (item.key === "all") {
        const count = allTabTotal !== null ? `(${allTabTotal})` : "";
        return { ...item, label: `All ${count}` };
      }
      return item;
    });
  }, [allTabTotal]);

  // --- row selection ---
  const rowSelection = {
    selectedRowKeys,
    onChange: (newKeys: React.Key[] = [], rows: Ticket[] = []) => {
      setSelectedRowKeys(newKeys);
      setSelectedRows(rows);
    },
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  useEffect(() => {
    const validKeys = new Set(normalizedData.map((r) => r.id));
    setSelectedRowKeys((keys) => (keys || []).filter((k) => validKeys.has(k)));
  }, [activeTab]);

  const menu = (
    <Menu
      onClick={({ key }) =>
        setFilters((prev) => ({ ...prev, ticketType: key }))
      }
      items={[
        { key: "Incident", label: "Incident" },
        { key: "Service request", label: "Service request" },
      ]}
    />
  );

  const TICKET_TYPE_MAP: Record<string, string> = {
    "1": "Service type",
    "2": "Incident",
  };

  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const columns: ColumnsType<Ticket> = useMemo(
    () => [
      {
        title: "ID No",
        dataIndex: "service_ticket_no",
        key: "service_ticket_no",
        width: 200,
        fixed: "left",
        ellipsis: true,
        render: (id: string | number, record: Ticket) => (
          <Tooltip title={id || "-"}>
            <span
              onClick={() => {
                navigate(
                  `/tickets/d/${
                    record.pikitup_ticket_id ?? record.pikitup_ticket_id
                  }`,
                  {
                    state: { record }, // ✅ pass the whole record
                  }
                );
              }}
              className="truncate-cell cursor-pointer flex items-center gap-2"
            >
              {id || "-"}
              {renderFlagIcon(record)}
            </span>
          </Tooltip>
        ),
      },
      {
        title: "Title",
        dataIndex: "service_short_description",
        key: "service_short_description",
        width: 180,
        fixed: "left",
        ellipsis: true,
        render: (text: string, record: Ticket) => {
          const titleText =
            text || record.customer_surname || record.title || "-";
          const idToUse = record.pikitup_ticket_id ?? record.id;
          return (
            <Tooltip title={titleText}>
              <span
                onClick={() => navigate(`/tickets/d/${idToUse}`)}
                className="truncate-cell cursor-pointer"
              >
                {titleText}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: "Location",
        dataIndex: "service_address_info",
        key: "location",
        width: 150,
        fixed: "left",
        ellipsis: true,
        render: (text: string) => (
          <Tooltip title={text || "-"}>
            <span className="truncate-cell">{text || "-"}</span>
          </Tooltip>
        ),
      },
      {
        title: "Ticket type",
        dataIndex: "service_type",
        key: "service_type",
        width: 150,
        ellipsis: true,
        render: (type: string | number) => {
          const label = TICKET_TYPE_MAP[type?.toString()] || "-";
          return (
            <Tooltip title={label}>
              <span className="truncate-cell">{label}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Unit group",
        dataIndex: "unit_group_name",
        key: "unit_group_name",
        width: 150,
        ellipsis: true,
      },
      {
        title: "Depot name",
        dataIndex: "depot_name",
        key: "depot_name",
        width: 150,
        ellipsis: true,
      },
      {
        title: "Created by",
        key: "createdBy",
        width: 150,
        ellipsis: true,
        render: (_: any, record: any) => {
          const name = `${record.customer_first_name || ""} ${
            record.customer_surname || ""
          }`.trim();
          return (
            <Tooltip title={name || "-"}>
              <span className="truncate-cell">{name || "-"}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "admin_internal_status",
        key: "admin_internal_status",
        width: 280,
        ellipsis: true,
        render: (_: string, record: Ticket) => {
          const statusLabel =
            STATUS_MAP[record.admin_internal_status ?? ""] || "-";
          // Only for Pending tickets (status_flg === 10)
          const showUnassignedTimer =
            (record.status_flg === 28 ||
              record.status_flg === 10 ||
              record.status_flg === 1) &&
            record.created_date &&
            record.created_time;

          const showUnassignedTimerResponse =
            (record.status_flg === 28 || record.status_flg === 1) &&
            record.created_date &&
            record.created_time;
          const formatCountdown = (targetTime: string) => {
            const now = dayjs();
            const escalationTime = dayjs(targetTime, "DD-MM-YYYY HH:mm:ss");

            // Calculate difference in seconds
            const diff = escalationTime.diff(now, "second");
            const dur = dayjs.duration(diff, "seconds");

            // ✅ Use total hours across days (not just within 24h)
            const hours = Math.floor(dur.asHours());
            const minutes = dur.minutes();
            const seconds = dur.seconds();

            // Format properly
            return `${hours.toString().padStart(2, "0")}:${minutes
              .toString()
              .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
          };

          const getUnassignedStatus = () => {
            const {
              resolution_sla_escalation_time,
              resolution_sla_overdue,
            }: any = record;

            // ❗ Condition: if null AND false → render nothing
            if (!resolution_sla_escalation_time && !resolution_sla_overdue) {
              return null;
            }

            // If time missing but overdue is true → show Breached
            if (!resolution_sla_escalation_time && resolution_sla_overdue) {
              return (
                <div className="sla_container">
                  Resolution: <span className="breached">Breached</span>
                </div>
              );
            }

            // Parse date strictly
            const escalationTime = dayjs(
              resolution_sla_escalation_time,
              "DD-MM-YYYY HH:mm:ss",
              true
            );

            if (!escalationTime.isValid()) return null;

            // Breached?
            if (resolution_sla_overdue || dayjs().isAfter(escalationTime)) {
              return (
                <div className="sla_container">
                  Resolution: <span className="breached">Breached</span>
                </div>
              );
            }

            const countdown = formatCountdown(resolution_sla_escalation_time);
            if (!countdown || countdown.includes("NaN")) return null;

            return (
              <div className="sla_container">
                Resolution: <span className="timer">{countdown}</span>
              </div>
            );
          };

          const getUnassignedStatusResponse = () => {
            const { response_sla_escalation_time, response_sla_overdue }: any =
              record;

            // ❗ If null AND false → show NOTHING
            if (!response_sla_escalation_time && !response_sla_overdue) {
              return null;
            }

            if (!response_sla_escalation_time && response_sla_overdue) {
              return (
                <div className="sla_container">
                  Response: <span className="breached">Breached</span>
                </div>
              );
            }

            const escalationTime = dayjs(
              response_sla_escalation_time,
              "DD-MM-YYYY HH:mm:ss",
              true
            );

            if (!escalationTime.isValid()) return null;

            if (response_sla_overdue || dayjs().isAfter(escalationTime)) {
              return (
                <div className="sla_container">
                  Response: <span className="breached">Breached</span>
                </div>
              );
            }

            const countdown = formatCountdown(response_sla_escalation_time);
            if (!countdown || countdown.includes("NaN")) return null;

            return (
              <div className="sla_container">
                Response: <span className="timer">{countdown}</span>
              </div>
            );
          };

          return (
            <div className="flex items-center gap-2">
              <Tooltip title={statusLabel}>
                <StatusPill status={record.admin_internal_status ?? ""} />
              </Tooltip>
              <div className="flex flex-col gap-1">
                {showUnassignedTimerResponse && getUnassignedStatusResponse()}
                {showUnassignedTimer && getUnassignedStatus()}
              </div>
            </div>
          );
        },
      },
      {
        title: "Assigned to",
        dataIndex: "assignee_name",
        key: "assignee_name",
        width: 200,
        ellipsis: true,
        render: (_: any, record: Ticket) => {
          return (
            <Tooltip title={record.assignee_name || "-"}>
              <span className="truncate-cell">
                {record.assignee_name || "-"}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: "Created on",
        dataIndex: "created_date",
        key: "createdOn",
        width: 150,
        ellipsis: true,
        render: (text: string) => (
          <Tooltip title={text || "-"}>
            <span className="truncate-cell">{text || "-"}</span>
          </Tooltip>
        ),
      },
      {
        title: "",
        key: "actions",
        width: 50,
        fixed: "right",
        render: (_: any, record: Ticket) => (
          <Dropdown overlay={ActionMenu(record)} trigger={["click"]}>
            <Button icon={<MoreOutlined />} type="text" />
          </Dropdown>
        ),
      },
    ],
    [navigate]
  );

  const columnsForTable = isLoading
    ? columns.map((col) => {
        if (col.key !== "actions") {
          return {
            ...col,
            ellipsis: false,
            render: () => (
              <Skeleton.Input
                active
                size="small"
                style={{ width: "100%", height: "20px" }}
              />
            ),
          };
        }
        return col;
      })
    : columns;

  //reset all filters
  const resetAllFilters = () => {
    setFilters({
      search: "",
      ticketType: null,
      columnFilters: {},
    });
    setFilterData({});
    setDebouncedSearch("");
    setSelectedRowKeys([]);
    setSelectedRows([]);
  };
  const TAB_ITEMS = useMemo(() => {
    if (isMemberType4) {
      // Supervisor view tabs
      return [
        { key: "all", label: "All" },
        { key: "new", label: "New Tickets" },

        { key: "assigned", label: "Assigned Tickets" },
        { key: "myAssigned", label: "My Assigned Tickets" },
        { key: "resolved", label: "My Resolved Tickets" },
        { key: "rejected", label: "My Rejected Tickets" },
        { key: "parked", label: "My Parked Tickets" },
      ];
    }

    // Default (BASE_TAB_ITEMS)
    return BASE_TAB_ITEMS.map((item) => {
      if (item.key === "all") {
        const count = allTabTotal !== null ? `(${allTabTotal})` : "";
        return { ...item, label: `All ${count}` };
      }
      return item;
    });
  }, [isMemberType4, allTabTotal]);

  return (
    <>
      {isPermit(2, "view") ? (
        <div className="tickets-dashboard">
          <div
            className={`tickets-table-header-container ${
              !pagination ? "flex items-baseline justify-between " : ""
            }`}
          >
            <div
              className={`tickets-header ${
                pagination ? "" : "w-fit justify-between "
              } `}
            >
              <div className=" w-full flex items-center gap-4">
                <h2 className="tickets-title">Tickets</h2>
                <ConfigProvider
                  theme={{
                    components: {
                      Tabs: {
                        inkBarColor: "#1677ff",
                        itemSelectedColor: "#1677ff",
                        itemHoverColor: "#4096ff",
                        itemColor: "rgba(0,0,0,0.88)",
                      },
                    },
                  }}
                >
                  <Tabs
                    onChange={(key) => {
                      setCurrentPage(1);
                      setActiveTab(key);
                      resetAllFilters();
                    }}
                    defaultActiveKey="all"
                    items={TAB_ITEMS}
                    className="status-tabs"
                    tabBarGutter={24}
                  />
                </ConfigProvider>
              </div>
              {/* {pagination && isPermit(2, "edit") && (
                <button
                  onClick={() => setDrawerOpen(true)}
                  className="assign-ticket-btn"
                >
                  ASSIGN TICKET
                </button>
              )} */}
            </div>
            <div className={`table-controls`}>
              {/* <div
                className={`search-wrapper ${isSearchActive ? "active" : ""}`}
                onClick={() => setIsSearchActive(true)}
              >
                {" "}
                <span>
                  {" "}
                  <HeaderSearchIcon width={20} height={20} />
                </span>
                <Input
                  placeholder="Search..."
                  className="search-input"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                {isSearchActive && (
                  <Button
                    className="clear-button"
                    icon={<CloseIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchValue("");
                      setIsSearchActive(false);
                    }}
                    type="text"
                  />
                )}
              </div> */}
              <div className="table-filter-wrapper flex gap-3">
                {selectedRowKeys.length >= 2 && isPermit(2, "merge") && (
                  <div className="merge-btn-wrapper">
                    <Button
                      onClick={() => setMergedDrawerOpen(true)}
                      icon={<MergedTicketIcon />}
                      className="transparent merge-ticket-btn"
                    >
                      MERGE REQUEST
                    </Button>
                  </div>
                )}

                {/* {selectedRowKeys.length >= 1 && (
                  <div className="assign-btn-wrapper">
                    <Button
                      onClick={() => setAssignConfirmOpen(true)}
                      icon={<Ticket />}
                      className="transparent assign-ticket-btn"
                    >
                      ASSIGN TICKET
                    </Button>
                  </div>
                )} */}

                <div className="left-controls">
                  {pagination && (
                    <>
                      {!filters.ticketType ? (
                        <Dropdown overlay={menu} trigger={["click"]}>
                          <Button
                            icon={<Ticket />}
                            className="transparent ticket-type-btn"
                          >
                            Ticket type
                          </Button>
                        </Dropdown>
                      ) : (
                        <TagComp
                          icon={<Ticket />}
                          onClose={() =>
                            setFilters((prev) => ({
                              ...prev,
                              ticketType: null,
                            }))
                          }
                          className="ticket-type-tag"
                        >
                          {filters.ticketType}
                        </TagComp>
                      )}
                    </>
                  )}
                  <FilterDropdown
                    tableHeaders={[
                      { label: "ID no.", value: "id" },
                      { label: "Title", value: "title" },
                      { label: "Created by", value: "createdBy" },
                      ...(activeTab === "all"
                        ? [{ label: "Status", value: "status" }]
                        : []),
                      { label: "Unit group", value: "unitGroup" },
                      { label: "Assigned to", value: "assignedTo" },
                      { label: "Created on", value: "createdOn" },
                    ]}
                    key={`filter-${activeTab}`}
                    optionsByColumn={{}}
                    currentPage={currentPage}
                    perPage={pageSize}
                    onChange={({ apiParams }) => {
                      setFilterData(apiParams);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <Table
            rowKey={(record) =>
              record.pikitup_ticket_id || record.id || `key-${Math.random()}`
            }
            loading={loadingState}
            columns={columnsForTable}
            dataSource={normalizedData}
            pagination={false}
            rowSelection={rowSelection}
            scroll={{
              x: 1400,
              y: "calc(100vh - 400px)",
            }}
          />
          {pagination && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 16,
              }}
            >
              <div
                style={{
                  color: "var(--Neutral-1000, #10100F)",
                  fontFamily: "Host Grotesk",
                  fontSize: 13,
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "110%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                Page
                <InputNumber
                  controls={false}
                  min={1}
                  max={
                    allTotalPagination
                      ? Math.ceil(allTotalPagination / pageSize)
                      : 1
                  }
                  value={currentPage}
                  onChange={(val) => setCurrentPage(val || 1)}
                  style={{ width: 60 }}
                />
                <span>
                  {" "}
                  of{" "}
                  {allTotalPagination
                    ? Math.ceil(allTotalPagination / pageSize)
                    : 1}
                </span>
              </div>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={allTotalPagination ? allTotalPagination : 0}
                onChange={(page) => setCurrentPage(page)}
                showSizeChanger={false}
              />
            </div>
          )}
        </div>
      ) : (
        <PermissionDenied />
      )}
      {/* <AssignTickets
        open={drawerOpen}
        setOpen={setDrawerOpen}
        title="Assign ticket"
      /> */}
      <MergeTickets
        open={mergedDrawerOpen}
        setOpen={setMergedDrawerOpen}
        title="Merge tickets"
        selectedTickets={selectedRows}
        allTickets={normalizedData}
        setSelectedRowKeys={setSelectedRowKeys}
        setSelectedRows={setSelectedRows}
      />

      <ChangeStatusModal
        loading={isUpdating}
        open={changeStatusModalOpen}
        onClose={() => setChangeStatusModalOpen(false)}
        onConfirm={({ status, description }) => {
          if (!selectedRecord) return;

          updateTicketStatusMutate(
            {
              service_ticket_no: selectedRecord.service_ticket_no,
              status: Number(status),
              comments: description || "",
            },
            {
              onSuccess: (res) => {
                message.success(
                  res.message || "Ticket status updated successfully"
                );
                setChangeStatusModalOpen(false);
                setSelectedRecord(null);
                refetchTickets();

                // queryClient.invalidateQueries(["tickets"]);
              },
              onError: () => {
                message.error("Failed to update ticket status");
              },
            }
          );
        }}
        mode={"rejected"}
      />
      {assignConfirmOpen && (
        <ConfirmModal
          open={assignConfirmOpen}
          title="Assign ticket"
          message={`You are about to assign ${selectedRowKeys.length} selected ticket(s).`}
          confirmText="Assign"
          loading={isAssigning}
          onClose={() => setAssignConfirmOpen(false)}
          onConfirm={() => {
            if (selectedRowKeys.length === 0) return;

            assignTicketMutate(
              {
                pikitup_ticket_ids: selectedRows.map((t) =>
                  Number(t.pikitup_ticket_id)
                ),
              },
              {
                onSuccess: (res) => {
                  message.success(
                    res.message || "Tickets assigned successfully"
                  );
                  setAssignConfirmOpen(false);
                  setSelectedRowKeys([]);
                  setSelectedRows([]);
                  refetchTickets();
                },
                onError: (err: any) => {
                  message.error(
                    err?.response?.data?.message ||
                      "Failed to assign tickets. Please try again."
                  );
                },
              }
            );
          }}
        />
      )}
    </>
  );
};

export default TicketsTable;
